import { useTranslation } from '../../../../lib/i18n';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { formatMoney } from '../../../../lib/money';
import { PickerTopup } from '../../../../lib/plans';
import { SETTINGS_PAGE } from '../settings-shared';

export interface BuyCreditsProps {
  open: boolean;
  onClose: () => void;
  topup: PickerTopup;
  planName: string;
  checkoutHref: string;
}

// Shared logic + copy for the "Buy more credits" top-up flow. The desktop centered modal
// (Figma 12889-206300) and the mobile bottom sheet (Figma 12252-58241) render different chrome but MUST
// behave identically — same BI events, same derived labels, same checkout deeplink — so every non-layout
// concern lives here and each surface only owns its own markup.
export function useBuyCredits({ onClose, topup, planName, checkoutHref }: Omit<BuyCreditsProps, 'open'>) {
  const { t } = useTranslation();
  const { logModalActionBi, logPageClickBi } = useGenericBi();

  const close = () => {
    logModalActionBi({ ...SETTINGS_PAGE, modal_name: 'buy_credits', action: 'close' });
    onClose();
  };

  // Check out hands off to the packages picker with this pack preselected — the same ?topUpPackageId
  // deeplink both native apps open. BI mirrors native's checkout event so the funnels join.
  const onCheckout = () => logPageClickBi({ ...SETTINGS_PAGE, button_name: 'buy_credits_checkout' });

  return {
    close,
    onCheckout,
    checkoutHref,
    title: t('settings.buyCredits.title'),
    subtitle: t('settings.buyCredits.subtitle', { plan: planName }),
    amountLabel: t('settings.buyCredits.amount', { credits: topup.credits.toLocaleString() }),
    priceLabel: formatMoney(topup.amount, topup.currency),
    oneTimeLabel: t('settings.buyCredits.oneTime'),
    checkoutLabel: t('settings.buyCredits.checkout'),
    closeLabel: t('common.close'),
  };
}
